/**
 * Far-left views rail — the 4-pane inbox's first column. Mirrors the storybook
 * `_conversations.tsx` `InboxNav` (grouped NavSection / NavRow / NavCount)
 * styling, scoped to the backed helpdesk views with client-derived counts.
 * Views are grouped by ownership: AI Agents · Inbox · Status.
 */
import { cn } from "@cometchat-ai/ui-dashboard";
import { useTranslation } from "react-i18next";

import { Check, MessagesSquare, Scale, Sparkles, User, Users } from "../components/icons";
import type { LucideIcon } from "../components/icons";

export type ViewId = "ai" | "queue" | "mine" | "all" | "decisions" | "resolved";

type ViewLabelKey =
  | "helpdesk.views.ai"
  | "helpdesk.views.queue"
  | "helpdesk.views.mine"
  | "helpdesk.views.all"
  | "helpdesk.views.decisions"
  | "helpdesk.views.resolved";

type GroupLabelKey =
  | "helpdesk.groups.aiAgents"
  | "helpdesk.groups.inbox"
  | "helpdesk.groups.status";

interface ViewDef {
  id: ViewId;
  icon: LucideIcon;
  labelKey: ViewLabelKey;
}

const VIEW_GROUPS: ReadonlyArray<{ labelKey: GroupLabelKey; views: ReadonlyArray<ViewDef> }> = [
  {
    labelKey: "helpdesk.groups.aiAgents",
    views: [
      { id: "ai", icon: Sparkles, labelKey: "helpdesk.views.ai" },
      { id: "decisions", icon: Scale, labelKey: "helpdesk.views.decisions" },
    ],
  },
  {
    labelKey: "helpdesk.groups.inbox",
    views: [
      { id: "queue", icon: MessagesSquare, labelKey: "helpdesk.views.queue" },
      { id: "mine", icon: User, labelKey: "helpdesk.views.mine" },
      { id: "all", icon: Users, labelKey: "helpdesk.views.all" },
    ],
  },
  {
    labelKey: "helpdesk.groups.status",
    views: [{ id: "resolved", icon: Check, labelKey: "helpdesk.views.resolved" }],
  },
];

/** Flat list of every view, for the page's counts + selection logic. */
export const VIEWS: ReadonlyArray<ViewDef> = VIEW_GROUPS.flatMap((g) => g.views);

function NavCount({ value }: { value: number }) {
  return (
    <span
      className={cn(
        "text-xs tabular-nums",
        value === 0 ? "text-[color:var(--color-fg-subtle)]" : "font-medium text-muted-foreground",
      )}
    >
      {value.toLocaleString()}
    </span>
  );
}

export function InboxViewsRail({
  view,
  counts,
  onSelect,
}: {
  view: ViewId;
  counts: Record<ViewId, number>;
  onSelect: (v: ViewId) => void;
}) {
  const { t } = useTranslation("workspace");
  return (
    <div className="flex w-52 min-w-44 shrink-0 flex-col border-r border-border">
      <div className="flex h-12 shrink-0 items-center border-b border-border px-4.5">
        <span className="text-base font-semibold tracking-[0.01em] text-foreground">
          {t("helpdesk.views.heading")}
        </span>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-auto px-1.5 py-3">
        {VIEW_GROUPS.map((group) => (
          <div key={group.labelKey} className="space-y-0.5">
            <div className="px-3 pb-1 text-2xs font-medium uppercase tracking-wide text-[color:var(--color-fg-subtle)]">
              {t(group.labelKey)}
            </div>
            {group.views.map((v) => {
              const active = v.id === view;
              const Icon = v.icon;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => onSelect(v.id)}
                  aria-current={active ? "page" : undefined}
                  data-testid={`helpdesk-view-${v.id}`}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-3 py-1.5 text-left text-2sm transition-colors",
                    active
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)] hover:text-foreground",
                  )}
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{t(v.labelKey)}</span>
                  <NavCount value={counts[v.id]} />
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
